import { useTranslations } from 'next-intl'

import classnames from 'classnames'

import type { currencyOptions } from '~/components/atoms/ArtworkCard/ArtworkCard'
import ArtworkPrice from '~/components/atoms/ArtworkPrice/ArtworkPrice'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import type { ImageProps } from '~/components/atoms/Image/Image'
import PortfolioTableArtwork from '~/components/atoms/PortfolioTableArtwork/PortfolioTableArtwork'
import PulsingDot, { DotSize } from '~/components/atoms/PulsingDot/PulsingDot'
import type { ExpandableColumn } from '~/components/atoms/Table/Table'
import Table from '~/components/atoms/Table/Table'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import TradingFloorCard from '~/components/atoms/TradingFloorCard/TradingFloorCard'
import ArtworkCardPortfolio from '~/components/blocks/ArtworkCardPortfolio/ArtworkCardPortfolio'
import type { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useFormAnalytics from '~/hooks/useFormAnalytics'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import capFirstLetter from '~/utils/capFirstLetter'
import { relativeDateFormat } from '~/utils/formatDate'
import ticketStatusMapper, {
  TradingTicketStatus,
} from '~/utils/ticketStatusMapper'

import useHooks from './hooks'

import styles from './TradingFloorList.module.css'

export const TradingFloorRelativeDate = ({ date }: { date: number }) => {
  return (
    <Text size={TextSizes.bodySml} uppercase className={styles.dateModified}>
      {relativeDateFormat(date).toLowerCase() === 'just now' && (
        <div className={styles.dateDot}>
          <PulsingDot size={DotSize.small} />
        </div>
      )}
      {relativeDateFormat(date)}
    </Text>
  )
}

export interface TradingFloorItemProps {
  ticketStatus: string
  artworkId: string
  dateModified: number
  _id: string
  artist: string
  artistId: string
  artistSlug: string
  artworkSlug: string
  collectionSlug?: string
  collectionTaxonomy?: string
  format: string
  image: ImageProps
  listPriceMax?: number
  listPriceMaxValues?: currencyOptions[]
  listPriceMin?: number
  listPriceMinValues?: currencyOptions[]
  poa?: boolean
  signed?: string
  title?: string
  year?: number
  objectID: string
  aagr?: number
}

export interface TradingFloorListProps {
  items: TradingFloorItemProps[]
  noContainer?: boolean
  title?: boolean
  summary?: string
  teaser?: string
  testID?: string
  viewAllLabel?: string
  viewAllLink?: string
}

type Listing = {
  artworkDetails?: React.ReactElement
  demandAndSupply?: JSX.Element
  estimatedValue?: React.ReactElement
  growth5Year?: React.ReactElement | string
}

interface TableViewProps {
  items: TradingFloorListProps['items']
  openForm: (supplyProps: FormTypes, item: TradingFloorItemProps) => void
}

const TableView = ({ items, openForm }: TableViewProps) => {
  const tCommon = useTranslations('common')
  const t = useTranslations('tradingFloorList')
  const columns: ExpandableColumn<Listing>[] = [
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('tradingStatus')}
        </Text>
      ),
      minWidth: 150,
      accessor: 'demandAndSupply',
    },
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('artwork')}
        </Text>
      ),
      minWidth: 250,
      accessor: 'artworkDetails',
    },
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('estimatedValue')}
        </Text>
      ),
      minWidth: 150,
      accessor: 'estimatedValue',
    },
    // {
    //   Header: (
    //     <Text uppercase size={TextSizes.bodySml}>
    //       {t('growthAagr')}
    //     </Text>
    //   ),
    //   minWidth: 100,
    //   accessor: 'growth5Year',
    //   isExpandable: true,
    //   expandableText: <EmptyGrowthMessage />,
    // },
    {
      Header: (
        <Text uppercase size={TextSizes.bodySml}>
          {t('date')}
        </Text>
      ),
      minWidth: 90,
      maxWidth: 90,
      accessor: 'dateModified',
    },
  ]
  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()

  const data =
    items &&
    items.map(item => {
      const status: any = item.ticketStatus
      const supplyProps = ticketStatusMapper(status, tCommon)

      if (!item.image) {
        return {}
      }
      return {
        demandAndSupply: (
          <div className={styles.tradingFloorFirstColumnContainer}>
            <Text
              size={TextSizes.secondaryTitleSml}
              Tag="h5"
              className={styles.supplyTitle}
            >
              {supplyProps.title}
            </Text>
            <div className={styles.tradingListFirstColumn}>
              <Button
                button={{
                  type: ButtonTypes.button,
                  onClick: async () => {
                    openForm(supplyProps.formType, item)
                    handleFormLaunchFromCtaAnalytics(
                      `Clicked Enquire to ${capFirstLetter(supplyProps.formType)}`,
                      supplyProps.formType,
                      'Trading Floor',
                      item.artist,
                      item.artistId,
                      item.title,
                      item.artworkId
                    )
                  },
                }}
                variant={
                  item.ticketStatus === TradingTicketStatus.sell
                    ? ButtonVariants.primaryDark
                    : ButtonVariants.secondaryDark
                }
                smallFont
                reducedPadding
                iconLeft
              >
                {supplyProps.buttonText}
              </Button>
              <div className={styles.artworkCardButtonsContainer}>
                <div className={styles.artworkCardButtons}>
                  <ArtworkCardPortfolio
                    artworkId={item.artworkId}
                    artworkImage={item.image}
                    artistId={item.artistId}
                    artistName={item.artist}
                    artworkName={item.title}
                    eventLocation={`Trading Floor Tile`}
                    artworkDetails={item}
                  />
                </div>
              </div>
            </div>
          </div>
        ),
        artworkDetails: (
          <PortfolioTableArtwork
            item={item}
            hasHistory={false}
            useInfoDrawer={false}
            linkArtwork
          />
        ),
        // growth5Year: item.aagr ? (
        //   <GrowthStatus size="medium" growth={+item.aagr} />
        // ) : (
        //   <GrowthStatus
        //     size="medium"
        //     growth={undefined}
        //     graphGrey
        //     showInfoIcon
        //     showEmptyText
        //   />
        // ),
        canExpand: item.aagr ? false : true,
        estimatedValue: (
          <ArtworkPrice
            listPriceMin={item.listPriceMin}
            listPriceMax={item.listPriceMax}
            listPriceMinValues={item.listPriceMinValues}
            listPriceMaxValues={item.listPriceMaxValues}
            poa={item.poa}
            poaPlaceHolder={t('priceDataUnavailable')}
          />
        ),
        dateModified: <TradingFloorRelativeDate date={item.dateModified} />,
      }
    })

  return (
    <Table<Listing>
      name="portfolio-collection"
      columns={columns}
      noMarginTop
      data={data}
      minWidth={900}
      expandedContent={() => (
        <div style={{ padding: '16px', backgroundColor: '#f8f8f8' }}>
          <Text>{t('additionalInformation')}</Text>
        </div>
      )}
    />
  )
}

export default function TradingFloorList({
  items,
  title,
  summary,
  teaser,
  testID,
  noContainer,
  viewAllLabel,
  viewAllLink,
}: TradingFloorListProps) {
  const { openForm } = useHooks()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const t = useTranslations('tradingFloorList')

  const renderMobileCards = () => {
    return items.map((item, i) => {
      if (!item.image) return null
      return (
        <TradingFloorCard
          item={item}
          key={`tradingfloor-${i}`}
          openForm={openForm}
        />
      )
    })
  }

  const renderInner = () => {
    return (
      <div className={styles.tradingFloorBase}>
        {title && (
          <div className={styles.titlewrap}>
            <div>
              <Text
                size={TextSizes.primaryTitleLrg}
                Tag="h3"
                className={styles.title}
              >
                {t('liveTrading')}
                <span>{t('floor')}</span>
              </Text>
              <PulsingDot size={DotSize.large} />
            </div>
            {viewAllLink && (
              <div
                className={classnames(!isMobile && styles.hideViewAllMobile)}
              >
                <Button
                  variant={ButtonVariants.textLinkSmallUnderline}
                  link={viewAllLink}
                  className={styles.viewAllButton}
                  linkOnclick={async () => {
                    await multiPostHogEventPusher([
                      {
                        eventType: `Trading Floor View All Clicked`,
                      },
                    ])
                  }}
                >
                  {viewAllLabel || t('viewAll')}
                </Button>
              </div>
            )}
          </div>
        )}
        {summary && (
          <Text size={TextSizes.bodyLrg} Tag="p" className={styles.summary}>
            {summary}
          </Text>
        )}

        {isMobile ? (
          renderMobileCards()
        ) : (
          <TableView items={items} openForm={openForm} />
        )}

        {teaser && (
          <div className={styles.btnWrap}>
            <Button
              iconLeft
              link={`/trading-floor`}
              linkOnclick={async () => {
                await multiPostHogEventPusher([
                  {
                    eventType: `Trading Floor Bottom View All Clicked`,
                  },
                ])
              }}
            >
              {t('viewAll')}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.base} data-testid={testID}>
      {noContainer ? (
        <>{renderInner()}</>
      ) : (
        <Container>{renderInner()}</Container>
      )}
    </div>
  )
}
